import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const BeforeAfterGallery = () => {
  const { currentLanguage } = useLanguage();
  const [activeIndex, setActiveIndex] = useState(0);
  const [sliderPosition, setSliderPosition] = useState(50);
  const containerRef = useRef(null);
  const isDragging = useRef(false);

  const content = {
    pt: {
      title: 'Transformações',
      subtitle: 'Veja como transformamos espaços em cenários de sonho',
      label: 'O nosso trabalho',
      before: 'Antes',
      after: 'Depois',
      drag: 'Arraste para comparar',
      items: [
        { title: 'Salão Rústico', desc: 'De espaço vazio a cenário de conto de fadas' },
        { title: 'Espaço Moderno', desc: 'Elegância contemporânea em cada detalhe' },
        { title: 'Jardim Exterior', desc: 'A magia da natureza iluminada' }
      ]
    },
    en: {
      title: 'Transformations',
      subtitle: 'See how we transform spaces into dream scenarios',
      label: 'Our work',
      before: 'Before',
      after: 'After',
      drag: 'Drag to compare',
      items: [
        { title: 'Rustic Hall', desc: 'From empty space to fairytale setting' },
        { title: 'Modern Space', desc: 'Contemporary elegance in every detail' },
        { title: 'Outdoor Garden', desc: 'The magic of illuminated nature' }
      ]
    },
    fr: {
      title: 'Transformations',
      subtitle: 'Découvrez comment nous transformons les espaces en décors de rêve',
      label: 'Notre travail',
      before: 'Avant',
      after: 'Après',
      drag: 'Faites glisser pour comparer',
      items: [
        { title: 'Salle Rustique', desc: 'D\'un espace vide à un décor de conte de fées' },
        { title: 'Espace Moderne', desc: 'Élégance contemporaine dans chaque détail' },
        { title: 'Jardin Extérieur', desc: 'La magie de la nature illuminée' }
      ]
    },
    es: {
      title: 'Transformaciones',
      subtitle: 'Mira cómo transformamos espacios en escenarios de ensueño',
      label: 'Nuestro trabajo',
      before: 'Antes',
      after: 'Después',
      drag: 'Arrastra para comparar',
      items: [
        { title: 'Salón Rústico', desc: 'De espacio vacío a escenario de cuento de hadas' },
        { title: 'Espacio Moderno', desc: 'Elegancia contemporánea en cada detalle' },
        { title: 'Jardín Exterior', desc: 'La magia de la naturaleza iluminada' }
      ]
    }
  };

  const c = content[currentLanguage] || content.pt;

  // Real before/after image URL
  const fullImageUrl = 'https://customer-assets.emergentagent.com/job_elegantweddings/artifacts/anyczemi_ChatGPT%20Image%208_01_2026%2C%2010_10_42.png';
  
  const imagePairs = [
    { id: 0, row: 'top' },
    { id: 1, row: 'middle' },
    { id: 2, row: 'bottom' }
  ];

  const nextSlide = () => {
    setActiveIndex((prev) => (prev + 1) % imagePairs.length);
    setSliderPosition(50);
  };

  const prevSlide = () => {
    setActiveIndex((prev) => (prev - 1 + imagePairs.length) % imagePairs.length);
    setSliderPosition(50);
  };

  const handleMouseDown = () => {
    isDragging.current = true;
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(5, Math.min(95, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      isDragging.current = false;
    };
    
    const handleGlobalMouseMove = (e) => {
      if (isDragging.current) {
        handleMove(e.clientX);
      }
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    document.addEventListener('mousemove', handleGlobalMouseMove);
    
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('mousemove', handleGlobalMouseMove);
    };
  }, []);

  // Get vertical position for each row (the image has 3 rows)
  const getVerticalOffset = () => {
    return activeIndex * 33.33;
  };

  return (
    <section className="section-padding bg-[#FAF9F7]">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-caption text-[#C4B5A0] mb-4">{c.label}</p>
          <h2 className="heading-secondary text-4xl md:text-5xl text-[#2C2C2C] mb-4">
            {c.title}
          </h2>
          <div className="divider" />
          <p className="text-body text-[#6B4E37]">{c.subtitle}</p>
        </div>

        {/* Gallery with Slider */}
        <div className="space-y-8">
          <div 
            ref={containerRef}
            className="relative aspect-[2/1] rounded-2xl overflow-hidden shadow-xl cursor-ew-resize select-none"
            onMouseDown={handleMouseDown}
            onTouchMove={(e) => handleMove(e.touches[0].clientX)}
            onTouchStart={handleMouseDown}
            onTouchEnd={handleMouseUp}
            data-testid="before-after-slider"
          >
            {/* AFTER Side (Right half of image - full width, clipped from right) */}
            <div className="absolute inset-0 overflow-hidden">
              <div 
                className="absolute inset-0"
                style={{
                  width: '200%',
                  left: '-100%',
                }}
              >
                <img
                  src={fullImageUrl}
                  alt={c.after}
                  className="w-full h-[300%] object-cover transition-transform duration-300"
                  style={{ 
                    transform: `translateY(-${getVerticalOffset()}%)`,
                  }}
                />
              </div>
            </div>
            
            {/* BEFORE Side (Left half of image - revealed by slider) */}
            <div 
              className="absolute inset-0 overflow-hidden"
              style={{ width: `${sliderPosition}%` }}
            >
              <div 
                className="absolute inset-0"
                style={{
                  width: `${containerRef.current ? (containerRef.current.offsetWidth * 2) : 2000}px`,
                  maxWidth: 'none',
                }}
              >
                <img
                  src={fullImageUrl}
                  alt={c.before}
                  className="w-full h-[300%] object-cover transition-transform duration-300"
                  style={{ 
                    transform: `translateY(-${getVerticalOffset()}%)`,
                  }}
                />
              </div>
            </div>

            {/* Slider Handle */}
            <div 
              className="absolute top-0 bottom-0 w-1 bg-white/90 shadow-lg cursor-ew-resize z-10"
              style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
            >
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center border-2 border-[#C4B5A0]">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-[#C4B5A0]">
                  <path d="M8 5L3 12L8 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 5L21 12L16 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>

            {/* Labels */}
            <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium z-20">
              {c.before}
            </div>
            <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm text-[#2C2C2C] px-4 py-2 rounded-full text-sm font-medium z-20">
              {c.after}
            </div>

            {/* Navigation Arrows */}
            <button 
              onClick={prevSlide}
              className="absolute left-4 bottom-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center hover:bg-white transition-colors z-20"
              aria-label="Previous"
            >
              <ChevronLeft className="w-5 h-5 text-[#6B4E37]" />
            </button>
            <button 
              onClick={nextSlide}
              className="absolute right-4 bottom-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center hover:bg-white transition-colors z-20"
              aria-label="Next"
            >
              <ChevronRight className="w-5 h-5 text-[#6B4E37]" />
            </button>

            {/* Drag hint */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 backdrop-blur-sm text-white/80 px-4 py-2 rounded-full text-xs z-20">
              ← {c.drag} →
            </div>
          </div>

          {/* Dots Navigation */}
          <div className="flex justify-center gap-3">
            {imagePairs.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setActiveIndex(index);
                  setSliderPosition(50);
                }}
                className={`w-3 h-3 rounded-full transition-all ${
                  activeIndex === index 
                    ? 'bg-[#C4B5A0] w-8' 
                    : 'bg-[#E8E4E0] hover:bg-[#D4C4B0]'
                }`}
                data-testid={`dot-${index}`}
                aria-label={`View transformation ${index + 1}`}
              />
            ))}
          </div>

          {/* Current item info */}
          <div className="text-center">
            <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#2C2C2C]">
              {c.items[activeIndex]?.title}
            </h3>
            <p className="text-[#8B7D6B]">
              {c.items[activeIndex]?.desc}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BeforeAfterGallery;
